// Geometry of the guide's mock screens on the 1280×720 stage. Scenes draw
// at these rectangles and the timeline aims the cursor and the spotlight at
// them, so both always agree.

// ── App shell ───────────────────────────────────────────────────────────
export const FRAME = {
  sidebar: { x: 12, y: 12, w: 220, h: 696 },
  navHome: { x: 24, y: 84, w: 196, h: 44 },
  navHomeChevron: { x: 186, y: 94, w: 26, h: 24 },
  navTodo: { x: 40, y: 134, w: 180, h: 36 },
  navProfile: { x: 24, y: 134, w: 196, h: 44 },
  navProfileOpen: { x: 24, y: 178, w: 196, h: 44 },
  collapse: { x: 219, y: 41, w: 26, h: 26 },
  footer: { x: 24, y: 600, w: 196, h: 92 },
  hall: { x: 24, y: 604, w: 196, h: 40 },
  signOut: { x: 24, y: 652, w: 196, h: 40 },
  topbar: { x: 244, y: 12, w: 1024, h: 48 },
  search: { x: 352, y: 18, w: 320, h: 36 },
  searchResults: { x: 352, y: 62, w: 320, h: 126 },
  hallBtn: { x: 866, y: 18, w: 36, h: 36 },
  homeBtn: { x: 908, y: 18, w: 36, h: 36 },
  bell: { x: 950, y: 18, w: 36, h: 36 },
  lang: { x: 992, y: 18, w: 80, h: 36 },
  theme: { x: 1078, y: 18, w: 36, h: 36 },
  user: { x: 1120, y: 18, w: 140, h: 36 },
  rightIcons: { x: 862, y: 14, w: 256, h: 44 },
  userMenu: { x: 1012, y: 64, w: 248, h: 186 },
  userMenuProfile: { x: 1024, y: 144, w: 224, h: 38 },
  userMenuSignOut: { x: 1024, y: 200, w: 224, h: 38 },
};

export const CONTENT_X = 256;
export const CONTENT_W = 1000;

// ── Dashboard ───────────────────────────────────────────────────────────
export const DASH = {
  hero: { x: 256, y: 72, w: 1000, h: 156 },
  tasks: { x: 256, y: 240, w: 1000, h: 78 },
  tasksButton: { x: 1104, y: 260, w: 128, h: 38 },
  guide: { x: 256, y: 330, w: 1000, h: 112 },
  guidePlay: { x: 272, y: 346, w: 128, h: 80 },
  guideChips: { x: 420, y: 402, w: 700, h: 30 },
  linksTitle: { x: 260, y: 456, w: 300, h: 16 },
  links: { x: 256, y: 478, w: 1000, h: 160 },
  link: (index) => ({ x: 256 + index * 253, y: 478, w: 241, h: 160 }),
  banner: { x: 256, y: 72, w: 1000, h: 70 },
};

// ── Profile page ────────────────────────────────────────────────────────
// `scroll` 0 → 1 moves the profile card up and out; the section nav sticks
// under the top bar (as on desktop) and the tab content follows it.
export const HERO_H = 330;
export const NAV_TOP_AT_REST = 416;
export const NAV_STICKY = 72;
export const navTopAt = (scroll) => NAV_TOP_AT_REST - (NAV_TOP_AT_REST - NAV_STICKY) * scroll;
export const heroTopAt = (scroll) => 72 - (NAV_TOP_AT_REST - NAV_STICKY) * scroll;

export const NAV_GROUPS = [
  { key: 'takvim-ajanda', w: 116, tabs: [['takvim', 100], ['ajandam', 96]] },
  { key: 'dosyalar', w: 136, tabs: null },
  { key: 'projelerim', w: 136, tabs: null },
  { key: 'izin-yonetimi', w: 96, tabs: [['izin-talep', 124], ['izin-taleplerim', 136]] },
  { key: 'masraf-yonetimi', w: 118, tabs: [['masraf-talep', 140], ['masraf-takip', 120]] },
  { key: 'iletisim-bildirim', w: 150, tabs: [['mesajlar', 100], ['bildirimler', 112], ['sistem-hata-bildirimleri', 108]] },
  { key: 'cv-yonetimi', w: 172, tabs: [['cv-tr', 104], ['cv-en', 118], ['gcp', 66], ['iata', 66]] },
];

export const groupOf = (tab) => NAV_GROUPS.find((group) => group.key === tab || group.tabs?.some(([key]) => key === tab));
export const navHeight = (group) => (group?.tabs ? 92 : 56);

/** Rectangles of the nav panel, its group pills and the sub-tabs of `groupKey`. */
export const navRects = (navTop, groupKey) => {
  const group = NAV_GROUPS.find((item) => item.key === groupKey);
  let x = 268;
  const pills = {};
  NAV_GROUPS.forEach((item) => {
    pills[item.key] = { x, y: navTop + 8, w: item.w, h: 38 };
    x += item.w + 6;
  });
  const tabs = {};
  if (group?.tabs) {
    let tx = 268;
    group.tabs.forEach(([key, w]) => {
      tabs[key] = { x: tx, y: navTop + 56, w, h: 30 };
      tx += w + 6;
    });
  }
  return { panel: { x: 256, y: navTop, w: 1000, h: navHeight(group) }, pills, tabs };
};

/** Top of the tab content under the nav. */
export const contentTop = (navTop, groupKey) => navTop + navHeight(NAV_GROUPS.find((item) => item.key === groupKey)) + 16;

/** Sticky-nav geometry of a tab (the hero scrolled away). */
export const TAB = (tab) => {
  const group = groupOf(tab);
  const nav = navRects(NAV_STICKY, group.key);
  return { ...nav, top: contentTop(NAV_STICKY, group.key) };
};

export const HERO = (top = 72) => ({
  card: { x: 256, y: top, w: 1000, h: HERO_H },
  avatar: { x: 280, y: top + 22, w: 76, h: 76 },
  camera: { x: 336, y: top + 78, w: 26, h: 26 },
  name: { x: 372, y: top + 26, w: 460, h: 72 },
  completion: { x: 852, y: top + 22, w: 204, h: 52 },
  password: { x: 1066, y: top + 28, w: 170, h: 40 },
  stats: { x: 280, y: top + 112, w: 952, h: 56 },
  stat: (index) => ({ x: 280 + index * 320, y: top + 112, w: 312, h: 56 }),
  contacts: { x: 280, y: top + 184, w: 952, h: 132 },
  contact: (index) => ({ x: 280 + (index % 3) * 320, y: top + 204 + Math.floor(index / 3) * 64, w: 312, h: 56 }),
  contactEdit: (index) => ({ x: 280 + (index % 3) * 320 + 238, y: top + 204 + Math.floor(index / 3) * 64 + 14, w: 64, h: 28 }),
});

// Contact tiles in order: Telefon, Acil durum telefonu, E-posta, Ev adresi, İş adresi, Çalıştığım il
export const CONTACT_PHONE = 0;
export const CONTACT_EMERGENCY = 1;
export const CONTACT_EMAIL = 2;
export const CONTACT_HOME = 3;
export const CONTACT_WORK = 4;
export const CONTACT_WORK_CITY = 5;

// Completion popover under the completion button
export const COMPLETION_POP = (top = 72) => ({
  panel: { x: 860, y: top + 82, w: 376, h: 372 },
  rows: { x: 876, y: top + 176, w: 344, h: 132 },
  actions: { x: 876, y: top + 330, w: 344, h: 112 },
});
